import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import { DailyDish, DailyDishPayload } from "./dailyDishes.type";
import { PaginationResults, CollectionData } from "@/lib/type";

export const dailyDishesApi = createApi({
  reducerPath: "dailyDishesApi",
  baseQuery: baseQueryWithLogout,
  tagTypes: ["DailyDish"],
  endpoints: (builder) => ({
    createDailyDish: builder.mutation<DailyDish, DailyDishPayload>({
      query: (data) => ({
        url: "/daily_dishes",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["DailyDish"],
    }),

    getDailyDish: builder.query<DailyDish, string>({
      query: (id) => `/daily_dishes/${id}`,
      providesTags: (_, __, id) => [{ type: "DailyDish", id }],
    }),

    updateDailyDish: builder.mutation<
      DailyDish,
      { id: string; data: Partial<DailyDishPayload> }
    >({
      query: ({ id, data }) => ({
        url: `/daily_dishes/${id}`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify(data),
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "DailyDish", id },
        "DailyDish",
      ],
    }),

    deleteDailyDish: builder.mutation<void, string>({
      query: (id) => ({
        url: `/daily_dishes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["DailyDish"],
    }),

    getRestaurantDailyDishes: builder.query<
      PaginationResults<DailyDish>,
      { restaurantId?: string; page?: number; limit?: number }
    >({
      query: ({ restaurantId, page = 1, limit = 30 }) => {
        const params = new URLSearchParams();
        if (page) params.append("page", page.toString());
        // The API renames API Platform's items-per-page parameter to `limit`
        // (config/packages/api_platform.yaml), so `itemsPerPage` is ignored.
        if (limit) params.append("limit", limit.toString());

        return `/restaurants/${restaurantId}/daily_dishes?${params.toString()}`;
      },
      providesTags: ["DailyDish"],
      transformResponse: (data: CollectionData<DailyDish>) => {
        return {
          results: data["hydra:member"],
          count: data["hydra:totalItems"],
        };
      },
    }),

    getActiveRestaurantDailyDishes: builder.query<DailyDish[], string>({
      query: (restaurantId) =>
        `/restaurants/${restaurantId}/daily_dishes/active`,
      providesTags: ["DailyDish"],
      transformResponse: (data: DailyDish[] | CollectionData<DailyDish>) => {
        if (Array.isArray(data)) return data;
        return data["hydra:member"] ?? [];
      },
    }),

    toggleDailyDishStatus: builder.mutation<
      DailyDish,
      { id: string; isActive: boolean }
    >({
      query: ({ id, isActive }) => ({
        url: `/daily_dishes/${id}`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: { isActive },
      }),
      invalidatesTags: ["DailyDish"],
    }),
  }),
});

export const {
  useCreateDailyDishMutation,
  useGetDailyDishQuery,
  useUpdateDailyDishMutation,
  useDeleteDailyDishMutation,
  useGetRestaurantDailyDishesQuery,
  useGetActiveRestaurantDailyDishesQuery,
  useToggleDailyDishStatusMutation,
} = dailyDishesApi;
